import React, { useState, useEffect } from 'react'

import { IconPlus, IconAddItem } from 'components/ui/icons'
import { Button, ButtonGroup } from 'components/ui/button'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'
import { SectionHeader } from 'components/ui/section-header'
import { StatusSummary } from 'components/ui/status-summary'

import { SharedActionCreators, sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore, GettyAppState } from 'domains/org/getty/store'
import { ValidationFailuresList } from 'domains/org/getty/components/publishing/validationsList'
import { ErrorsList } from 'domains/org/getty/components/publishing/errorsList'

import { Page } from 'generated/mos/pagination'
import { ListToursRequest } from 'generated/mos/org/getty/audioaccess'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'

import { bffEndpoint } from 'helpers/bff'
import { statusSelector, Status } from 'helpers/status'
import { pick, pluralise, isNotNullOrUndefined } from 'helpers/core'
import { space } from 'helpers/style'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { TourCreate } from './tour-create'
import { TourCard } from './tour-card'
import { getSubnav } from '../subnav'

import styled from 'styled'

// TODO duplicated from edit-project/index.tsx
const FilterButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -${space(6)};
  margin-bottom: ${space(3)};
`;

const TourCardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: ${space(-3)};
  margin-right: ${space(-3)};
`;

type ActionProps =
  Pick<GettyActionCreators, 'gettyListToursRequest'| 'gettyDeleteTourReset' | 'gettyListValidationFailuresRequest'> &
  Pick<SharedActionCreators, 'toastNotification'>

type ConnectedProps = Pick<GettyAppState, 'tours' | 'tourDelete' | 'validationFailuresList' >

type DirectProps = {
  projectId: string;
  historyPush: (id: string) => void;
  navBackRoute?: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

const ToursPageView = (props: Props) => {
  const {
    projectId,
    gettyListToursRequest,
    toastNotification,
    tourDelete,
    gettyDeleteTourReset,
    validationFailuresList,
    tours,
    gettyListValidationFailuresRequest,
  } = props

  const [ tourCreate, setTourCreate ] = useState(false)

  const data = statusSelector.data(props.tours)
  const validationFailuresListData = statusSelector.data(validationFailuresList)
  const toursCount = data ? data.length : 0

  const hostName = bffEndpoint(window.location.hostname)

  useEffect(() => {
    gettyListToursRequest({
      ...ListToursRequest.defaults,
    })

    if (tourDelete.status === Status.Complete) {
      toastNotification({
        type: 'success',
        text: 'Tour deleted successfully',
      })
      gettyDeleteTourReset()
    }
  }, [gettyListToursRequest, projectId, toastNotification, tourDelete]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tours.status === Status.Ready) {
      const entityRefs = tours.data.map(({ ref }) => ref).filter(isNotNullOrUndefined)
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs,
        }
      })
    }
  }, [gettyListValidationFailuresRequest, tours]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LayoutListOrFormPage
      nav={() => (
        <PrimaryNavigationEditEntity
          title="Edit Project"
          navBackRoute={props.navBackRoute}
          darkTheme
        />
      )}
      title="GettyGuide"
      subnav={() => (
        <>
          <ErrorsList projectId={projectId} />
          <SecondaryNavigation navItems={getSubnav(projectId)} >
            <Button variant="subtle" onClick={() => setTourCreate(true)}>
              <IconPlus size="small" />create tour
            </Button>
            <Button variant="subtle" onClick={() => props.historyPush(`/project/${projectId}/categories`)}>
              <IconAddItem size="small" />Manage categories
            </Button>
          </SecondaryNavigation>
        </>
      )}
      toolbar={() => (
        <ButtonGroup>
          <ValidationFailuresList projectId={projectId} />
          <Button appearance="primary" onClick={() => props.historyPush(`/project/${projectId}/publishing`)}>
            Publish
          </Button>
        </ButtonGroup>
      )}
      content={() => (
        <>
          {tourCreate &&
            <TourCreate
              historyPush={props.historyPush}
              onClose={() => {
                setTourCreate(false)
              }}
              onSave={() => {
                setTourCreate(false)
              }}
              projectId={projectId}
            />
          }

          <StatusGuard status={tours}>
            <SectionHeader heading={`${toursCount} ${pluralise(toursCount, "tour", "tours")}`} />
            <TourCardGrid>
              {data && data.map(tour => (
                <TourCard
                  tour={tour}
                  projectId={props.projectId}
                  key={tour.ref && tour.ref.id}
                  hasWarning={validationFailuresListData && !!validationFailuresListData.find(({ ref }: ValidationFailure.Entity) => ref && tour.ref && ref.id === tour.ref.id)}
                  hostName={hostName}
                />
              ))}
            </TourCardGrid>
          </StatusGuard>
        </>
      )}
    />
  )
}

export const ToursPage = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, 'tours', 'tourDelete', 'validationFailuresList'),
  }),
  {
    ...pick(actionCreators, 'gettyListToursRequest', 'gettyDeleteTourReset', 'gettyListValidationFailuresRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(ToursPageView)
